import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import moment from 'moment';
import DOMUtils from '@spothero/utils/dom';
import GTMUtils from 'utils/gtm';
import SegmentUtils from 'utils/segment';
import StorageUtils from '@spothero/utils/storage';
import Alert from '@spothero/ui/alert';
import TextButton from '@spothero/ui/text-button';
import UserUtils from 'user/user-utils';
import DateUtils from 'utils/date';
import SpotUtils from 'utils/spot';
import {getStore} from 'store/store';
import Icon from 'common/icon/Icon';
import StarRating from 'common/star-rating/StarRating';
import SearchControlsModalMobile from 'search/controls/SearchControlsModalMobile';
import {push} from 'router/router-actions';
import AccessHours from './access/AccessHours';
import AmenitiesListMobile from './details/AmenitiesListMobile';
import ProximityInformation from './details/ProximityInformation';
import SaveAsFavorite from './details/SaveAsFavorite';
import SpotImagesMobile from './details/SpotImagesMobile';
import ThingsYouShouldKnow from './details/ThingsYouShouldKnow';
import GettingThere from './getting-there/GettingThere';
import HowToRedeem from './redeem/HowToRedeem';
import OnlineCommuterRateModal from './modals/OnlineCommuterRateModal';
import OversizedFeeDetailsModal from './modals/OversizedFeeDetailsModal';
import RateDetailsModal from './modals/RateDetailsModal';
import PriceAndTermsMobile from './reservation-details/PriceAndTermsMobile';
import {
    setSpotSelectedRate,
    cancelSpotRequests
} from 'spots/spots-actions';

class SpotDetailsPanelMobile extends Component {
    static propTypes = {
        spot: PropTypes.object,
        user: PropTypes.object,
        isMonthly: PropTypes.bool.isRequired,
        timezone: PropTypes.string.isRequired,
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string,
        setNewSpotRate: PropTypes.func.isRequired,
        onSearchClick: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func.isRequired,
        cancelAllSpotRequests: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        event: PropTypes.object
    };

    constructor(props) {
        super(props);

        const {
            spot,
            starts,
            ends
        } = props;

        this.state = {
            prevSpot: spot,
            starts,
            ends,
            updatedSpotData: null,
            isShowing: false,
            showSpotUnavailable: false,
            searchControlsLoading: false,
            showSearchControlsModal: false,
            showRateDetailsModal: false,
            showOnlineCommuterRateModal: false,
            showVehicleRestrictionsModal: false,
            rateDetailsSection: null
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            spot: nextSpot,
            starts: nextStarts,
            ends: nextEnds
        } = nextProps;
        const {
            prevSpot,
            starts,
            ends
        } = prevState;
        const newState = {};

        if (nextSpot.spotId !== prevSpot.spotId) {
            newState.updatedSpotData = null;
            newState.rateDetailsSection = null;
            newState.prevSpot = nextSpot;
        }

        if (nextStarts !== starts) {
            newState.starts = nextStarts;
        }

        if (nextEnds !== ends) {
            newState.ends = nextEnds;
        }

        if (!prevSpot.hasTravelDistanceUpdated && nextSpot.hasTravelDistanceUpdated) {
            newState.updatedSpotData = nextProps.spot;
        }

        return (!isEmpty(newState)) ? newState : null;
    }

    componentDidMount() {
        this._initSpotDetails();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            spot: {
                spotId
            }
        } = this.props;
        const {
            spot: {
                spotId: prevSpotId
            }
        } = prevProps;

        if (prevSpotId !== spotId) {
            this._initSpotDetails();
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        if (this._rateLoader) {
            this._rateLoader.cancel();
            this._rateLoader = null;
        }
    }

    _onMultiRateChange = rateId => {
        const {
            isMonthly,
            setNewSpotRate
        } = this.props;
        const {
            updatedSpotData
        } = this.state;
        const rateType = (isMonthly) ? 'monthly_rates' : 'hourly_rates';
        const newRate = find(updatedSpotData[rateType], {fullRule: parseInt(rateId, 10)});
        const newSpotData = {
            ...updatedSpotData,
            selectedRate: false
        };

        if (newRate) {
            newRate.fullUrl = SpotUtils.buildCheckoutURL(newSpotData, newRate, getStore().getState().searchRequest);
            newSpotData.selectedRate = newRate;
        }

        this.setState({
            updatedSpotData: newSpotData
        });

        setNewSpotRate(newSpotData);
    }

    _onSearchClick = ({starts, ends, callback}) => {
        this.props.onSearchClick();

        this.setState({
            searchControlsLoading: true,
            starts,
            ends
        });

        this._loadRates({
            alternateStarts: starts,
            alternateEnds: ends,
            callback
        });
    }

    _onChangeDatetimeClick = () => {
        this.setState({
            showSearchControlsModal: true
        });
    }

    _onHideSearchControlsModal = () => {
        this.setState({
            showSearchControlsModal: false
        });
    }

    _onShowRateDetailsClick = section => {
        this.setState({
            showRateDetailsModal: true,
            rateDetailsSection: (isString(section)) ? section : null
        });
    }

    _onOnlineCommuterRateClick = () => {
        this.setState({
            showOnlineCommuterRateModal: true
        });
    }

    _onVehicleRestrictionsClick = () => {
        this.setState({
            showVehicleRestrictionsModal: true
        });
    }

    _onBookSpotClick = evt => {
        evt.preventDefault();

        const {
            spot: {
                distanceInMiles
            },
            cancelAllSpotRequests,
            pushTo
        } = this.props;

        cancelAllSpotRequests();

        StorageUtils.set('spotDistanceinMiles', distanceInMiles);

        pushTo(DOMUtils.attr(evt.target, 'href'));
    }

    _onHideAllModals = () => {
        this.setState({
            showRateDetailsModal: false,
            showOnlineCommuterRateModal: false,
            showVehicleRestrictionsModal: false
        });
    }

    _initSpotDetails() {
        this._loadRates();
        this.setState({
            isShowing: true
        });

        SegmentUtils.track({
            event: 'opened_spot_details'
        });
    }

    _loadRates({alternateStarts, alternateEnds, callback} = {}) {
        const {
            spot,
            isMonthly,
            event,
            timezone
        } = this.props;
        const eventId = (event) ? event.id : null;
        let {
            starts,
            ends
        } = this.state;

        if (alternateStarts && alternateEnds) {
            starts = alternateStarts;
            ends = alternateEnds;
        }

        this._rateLoader = SpotUtils.loadRates({
            spotId: spot.spotId,
            starts,
            ends,
            isMonthly,
            timezone,
            searchDataParams: {
                monthly: isMonthly,
                starts,
                ends,
                eid: eventId
            },
            spot,
            eventId,
            onSuccessWithRates: updatedSpotData => {
                this.setState({
                    searchControlsLoading: false,
                    showSearchControlsModal: false,
                    updatedSpotData
                });

                this._rateLoader = null;

                if (callback) {
                    callback();
                }
            },
            onSuccessWithoutRates: () => {
                GTMUtils.push({
                    event: 'GAEvent',
                    eventCategory: 'automatic',
                    eventAction: 'rate_unavailable',
                    eventLabel: (isMonthly) ? 'monthly_rate_toggle' : 'transient_rate_toggle',
                    eventValue: undefined, // eslint-disable-line no-undefined
                    hitCallback: undefined // eslint-disable-line no-undefined
                });

                this.setState({
                    searchControlsLoading: false,
                    showSpotUnavailable: true,
                    showSearchControlsModal: true
                });

                this._rateLoader = null;

                if (callback) {
                    callback();
                }
            },
            onError: error => {
                if (this._isUnmounted) { return; }

                this.setState({
                    searchControlsLoading: false,
                    showSpotUnavailable: true,
                    showSearchControlsModal: true
                });

                this._rateLoader = null;
            }
        });
    }

    render() {
        const {
            user,
            spot,
            isMonthly,
            onSpotDetailsHidden
        } = this.props;
        const {
            updatedSpotData,
            isShowing,
            showSearchControlsModal,
            showRateDetailsModal,
            showOnlineCommuterRateModal,
            showVehicleRestrictionsModal,
            showSpotUnavailable,
            searchControlsLoading,
            rateDetailsSection
        } = this.state;
        const spotData = (updatedSpotData) ? updatedSpotData : spot;
        const {
            selectedRate,
            facility,
            distance,
            distanceInMiles,
            drivingDuration,
            walkingDuration,
            available_transient_inventory: inventory
        } = spotData;
        const {
            parking_spot_id: facilityId,
            title: facilityTitle,
            images: facilityImages,
            rating_info: {
                star_rating: starRating,
                number_of_ratings: numberOfRatings
            }
        } = facility;
        const classes = classNames(
            'SpotDetailsPanel',
            {'SpotDetailsPanel-showing': isShowing}
        );
        const bookSpotClasses = classNames(
            'book-spot',
            {'book-spot-showing': isShowing}
        );

        if (!selectedRate) {
            return null;
        }

        const dateTimeFormatted = DateUtils.getHumanDateTimesAsStrings({
            startDateTimeObj: moment(selectedRate.starts),
            endDateTimeObj: moment(selectedRate.ends),
            isMonthly
        });
        const {
            startDate,
            startTime,
            endDate,
            endTime
        } = dateTimeFormatted;
        const showAccessHours = ((selectedRate && (selectedRate.access_hours_formatted ||
            selectedRate.display_hours_comment)) ||
            (facility && facility.access_hours_formatted));
        const hasRedemption = (selectedRate)
            ? ((isMonthly && (selectedRate.redemption_instructions.length ||
                selectedRate.redemption_instructions_long_term.length)) ||
                (!isMonthly && (!isUndefined(facility.redemption_instructions) ||
                !isEmpty(facility.post_purchase_instructions))))
            : false;

        return (
            <span>
                <div className={classes}>
                    <SpotImagesMobile
                        key={facilityId}
                        images={facilityImages}
                        alt={facilityTitle}
                    />
                    {inventory > 0 && inventory < 4 &&
                        <Alert
                            className="remaining-spots"
                            type="highlight"
                            size="sm"
                        >
                            {inventory} {`spot${(inventory > 1) ? 's' : ''}`} left
                        </Alert>
                    }
                    <div
                        className="SpotDetailsPanel-datetime"
                        onClick={this._onChangeDatetimeClick}
                    >
                        <span className="SpotDetailsPanel-datetime-content">
                            <Icon name="clock" />
                            {!isMonthly &&
                                `${startDate}, ${startTime} - ${(startDate !== endDate) ? `${endDate},` : ''} ${endTime}`
                            }
                            {isMonthly && `Month Begins: ${startDate}`}
                        </span>
                    </div>
                    <div className="container">
                        <PriceAndTermsMobile
                            className="well well-light"
                            isMonthly={isMonthly}
                            spot={spotData}
                            onMultiRateChange={this._onMultiRateChange}
                            onRateDetailsClick={this._onShowRateDetailsClick}
                            onOnlineCommuterRateClick={this._onOnlineCommuterRateClick}
                            onVehicleRestrictionsClick={this._onVehicleRestrictionsClick}
                            checkoutUrl={selectedRate.fullUrl}
                        />
                        <div className="well well-light SpotDetailsPanel-info">
                            <h3 className="heading-md SpotDetailsPanel-title">
                                {(selectedRate.newMonthlyRate)
                                    ? selectedRate.title
                                    : facility.title
                                }
                            </h3>
                            {numberOfRatings > 0 &&
                                <span>
                                    <StarRating
                                        stars={starRating}
                                    />
                                    <small className="SpotListItem-rating-count">({numberOfRatings} ratings)</small>
                                </span>
                            }
                            <ProximityInformation
                                amenities={selectedRate.amenities}
                                distance={distance}
                                distanceInMiles={distanceInMiles}
                                drivingDuration={drivingDuration}
                                walkingDuration={walkingDuration}
                            />
                        </div>
                        {(user.status === UserUtils.AUTH_STATE.USER) &&
                            <div className="well well-light SpotDetailsPanel-favorite">
                                <SaveAsFavorite
                                    spotId={facilityId}
                                />
                            </div>
                        }
                        <ThingsYouShouldKnow
                            className="well well-light"
                            rate={selectedRate}
                            facility={facility}
                            isMonthly={isMonthly}
                            onShowRateDetailsClick={this._onShowRateDetailsClick}
                        />
                        <div className="well well-light SpotDetailsPanel-amenities">
                            <AmenitiesListMobile
                                rate={selectedRate}
                                isMonthly={isMonthly}
                                nonRateAmenities={facility.amenities_full}
                            />
                        </div>
                        {showAccessHours &&
                            <AccessHours
                                className="well well-light"
                                rate={selectedRate}
                                facility={facility}
                                sectionTitle="Access Hours"
                            />
                        }
                        {facility.getting_here &&
                            <GettingThere
                                className="well well-light"
                                rate={selectedRate}
                                facility={facility}
                                isMonthly={isMonthly}
                                sectionTitle="Getting There"
                            />
                        }
                        {hasRedemption &&
                            <HowToRedeem
                                className="well well-light"
                                rate={selectedRate}
                                facility={facility}
                                isMonthly={isMonthly}
                                sectionTitle="How to Redeem"
                            />
                        }
                    </div>
                    {showSearchControlsModal &&
                        <SearchControlsModalMobile
                            onHidden={this._onHideSearchControlsModal}
                            datetimeOnly
                            onSearchClick={this._onSearchClick}
                            onClosePanelClick={onSpotDetailsHidden}
                            showSpotUnavailable={showSpotUnavailable}
                            isLoading={searchControlsLoading}
                        />
                    }
                    {showRateDetailsModal &&
                        <RateDetailsModal
                            spot={spotData}
                            section={rateDetailsSection}
                            onHidden={this._onHideAllModals}
                        />
                    }
                    {showVehicleRestrictionsModal &&
                        <OversizedFeeDetailsModal
                            title="Restrictions and Fees"
                            spot={spotData}
                            onHidden={this._onHideAllModals}
                            hideContentTitle
                        />
                    }
                    {showOnlineCommuterRateModal &&
                        <OnlineCommuterRateModal
                            title="Commuter Rate"
                            description={selectedRate.online_commuter_rate_description}
                            onHidden={this._onHideAllModals}
                            hideContentTitle
                        />
                    }
                </div>
                <TextButton
                    className={bookSpotClasses}
                    href={selectedRate.fullUrl}
                    onClick={this._onBookSpotClick}
                >
                    Book It
                </TextButton>
            </span>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {
            data: {
                timezone
            }
        },
        user: {
            data: userData
        },
        event: {
            data: eventData
        },
        searchRequest: {
            monthly,
            starts,
            ends
        }
    } = state;

    return {
        user: userData,
        isMonthly: monthly,
        starts,
        ends,
        event: eventData,
        timezone
    };
};

const mapDispatchToProps = {
    setNewSpotRate: setSpotSelectedRate,
    cancelAllSpotRequests: cancelSpotRequests,
    pushTo: push
};

export default connect(mapStateToProps, mapDispatchToProps)(SpotDetailsPanelMobile);
